import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Card, Empty, Table, Tooltip, Typography } from 'antd'
import { getRetentionMetrics, MetricsPeriod } from '../../../Actions/operationsMetricsApi'
import { formatNumber } from './formatters'

interface Props {
  period: MetricsPeriod
  maxWeeks: number
}

interface CohortRow {
  key: string
  cohort: string
  signups: number
  /** weekNumber → { rate, activeUsers } · 미경과 주차는 키 자체가 없음 */
  weekMap: Record<number, { rate: number; activeUsers: number }>
}

/** retentionRate(0~1) → 셀 배경색. 진할수록 잔존율 높음 */
const cellStyle = (rate: number): React.CSSProperties => ({
  backgroundColor: `rgba(22, 119, 255, ${Math.max(0.06, rate)})`,
  color: rate > 0.5 ? '#fff' : 'inherit',
})

/**
 * 🔁 리텐션 — 가입 코호트(ISO 주차)별 N주차 잔존율 히트맵.
 * 잔존 기준 = 그 주에 크레딧을 소비함(영상/AI이미지/Vgen). 단순 로그인·무과금 News 제외.
 */
export default function RetentionSection({ period, maxWeeks }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['operationsMetrics', 'retention', period, maxWeeks],
    queryFn: () => getRetentionMetrics({ ...period, maxWeeks }),
  })

  const { rows, maxWeek } = useMemo(() => {
    const cohorts = data?.cohorts ?? []
    let max = 0
    const mapped: CohortRow[] = cohorts.map((c) => {
      const weekMap: CohortRow['weekMap'] = {}
      c.weeks.forEach((w) => {
        weekMap[w.weekNumber] = { rate: w.retentionRate, activeUsers: w.activeUsers }
        if (w.weekNumber > max) max = w.weekNumber
      })
      return { key: c.cohort, cohort: c.cohort, signups: c.signups, weekMap }
    })
    return { rows: mapped, maxWeek: max }
  }, [data])

  const columns = useMemo(() => {
    const base = [
      { title: '코호트', dataIndex: 'cohort', key: 'cohort', fixed: 'left' as const, width: 110 },
      {
        title: '가입',
        dataIndex: 'signups',
        key: 'signups',
        align: 'right' as const,
        width: 70,
        fixed: 'left' as const,
        render: (v: number) => formatNumber(v),
      },
    ]
    const weekCols = Array.from({ length: maxWeek + 1 }, (_, w) => ({
      title: `W${w}`,
      key: `w${w}`,
      align: 'center' as const,
      width: 64,
      onCell: (row: CohortRow) => {
        const cell = row.weekMap[w]
        return cell ? { style: cellStyle(cell.rate) } : {}
      },
      render: (_: unknown, row: CohortRow) => {
        const cell = row.weekMap[w]
        // 미경과 주차는 "데이터 없음" — 0%로 칠하지 않는다
        if (!cell) return <span style={{ color: '#ccc' }}>·</span>
        return (
          <Tooltip title={`${formatNumber(cell.activeUsers)} / ${formatNumber(row.signups)}`}>
            <span>{(cell.rate * 100).toFixed(0)}%</span>
          </Tooltip>
        )
      },
    }))
    return [...base, ...weekCols]
  }, [maxWeek])

  if (error)
    return <Alert type="error" message="리텐션 지표를 불러오지 못했습니다." description={(error as Error).message} />

  return (
    <Card title="🔁 리텐션 (가입 코호트 × 주차 · 크레딧 소비 기준)" size="small">
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
        잔존 = 해당 주차에 크레딧을 소비함(영상/AI이미지/Vgen). 단순 로그인·무과금 News 제외. 빈 칸(·)은 아직 경과하지 않은
        주차로 데이터 없음. 의미 있는 히트맵을 보려면 기간을 90일 이상으로 넓게 잡으세요.
      </Typography.Paragraph>
      {rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="기간 내 완결된 코호트 없음" />
      ) : (
        <Table
          columns={columns}
          dataSource={rows}
          size="small"
          pagination={false}
          loading={isLoading}
          scroll={{ x: 'max-content' }}
        />
      )}
    </Card>
  )
}
